<!-- components/forms/SMSForm.vue -->
<script setup>
import { ref, computed, watch } from "vue";
import {
  ChatBubbleLeftIcon,
  PhoneIcon,
  ExclamationCircleIcon,
} from "@heroicons/vue/24/outline";

// Props & Emits
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ phone: "", message: "" }),
  },
});

const emit = defineEmits(["update:modelValue"]);

// Reactive state
const phone = ref(props.modelValue.phone || "");
const message = ref(props.modelValue.message || "");
const phoneValidation = ref("");
const messageValidation = ref("");

// Computed
const isValidPhone = computed(() => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.value.replace(/\D/g, ""));
});

const formattedPhone = computed(() => {
  const digits = phone.value.replace(/\D/g, "");
  if (digits.length >= 10) {
    return `+${digits.slice(0, -10)} (${digits.slice(-10, -7)}) ${digits.slice(
      -7,
      -4
    )}-${digits.slice(-4)}`;
  }
  return phone.value;
});

const messageLength = computed(() => message.value.length);
const maxMessageLength = 160;

const smsUrl = computed(() => {
  if (!phone.value) return "";
  const cleanPhone = phone.value.replace(/\D/g, "");
  const encodedMessage = encodeURIComponent(message.value);
  return `sms:${cleanPhone}${message.value ? `?body=${encodedMessage}` : ""}`;
});

// Watchers
watch(
  [phone, message],
  ([newPhone, newMessage]) => {
    const smsData = {
      phone: newPhone,
      message: newMessage,
      url: smsUrl.value,
    };
    emit("update:modelValue", smsData);
    validateForm();
  },
  { immediate: true }
);

// Methods
const validateForm = () => {
  // Phone validation
  if (!phone.value) {
    phoneValidation.value = "Phone number is required";
  } else if (!isValidPhone.value) {
    phoneValidation.value = "Invalid phone number format";
  } else {
    phoneValidation.value = "";
  }

  // Message validation
  if (message.value.length > maxMessageLength) {
    messageValidation.value = `Message too long (max ${maxMessageLength} characters)`;
  } else {
    messageValidation.value = "";
  }
};

const formatPhoneNumber = () => {
  // Remove all non-digits
  const digits = phone.value.replace(/\D/g, "");

  // Format based on length
  if (digits.length >= 10) {
    const country = digits.slice(0, -10);
    const area = digits.slice(-10, -7);
    const exchange = digits.slice(-7, -4);
    const number = digits.slice(-4);

    phone.value = country
      ? `+${country} (${area}) ${exchange}-${number}`
      : `(${area}) ${exchange}-${number}`;
  }
};

const clearForm = () => {
  phone.value = "";
  message.value = "";
};

const useTemplate = (template) => {
  message.value = template;
};

const messageTemplates = [
  "Hi! Check out this link:",
  "Your verification code is:",
  "Reminder: Your appointment is scheduled for",
  "Thank you for your purchase!",
  "Welcome to our service!",
];
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center space-x-2 mb-4">
      <ChatBubbleLeftIcon class="w-5 h-5 text-green-600" />
      <h3 class="text-lg font-semibold text-gray-800">SMS Information</h3>
    </div>

    <!-- Phone Number -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700">
        Phone Number <span class="text-red-500">*</span>
      </label>

      <div class="relative">
        <div
          class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
        >
          <PhoneIcon class="h-5 w-5 text-gray-400" />
        </div>

        <input
          v-model="phone"
          type="tel"
          placeholder="+1 (555) 123-4567"
          @blur="formatPhoneNumber"
          class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
          :class="{
            'border-red-300 focus:ring-red-500 focus:border-red-500':
              phoneValidation,
            'border-green-300 focus:ring-green-500 focus:border-green-500':
              isValidPhone,
          }"
        />
      </div>

      <!-- Phone validation -->
      <div
        v-if="phoneValidation"
        class="flex items-center space-x-1 text-sm text-red-600"
      >
        <ExclamationCircleIcon class="w-4 h-4" />
        <span>{{ phoneValidation }}</span>
      </div>

      <!-- Formatted preview -->
      <div
        v-if="isValidPhone && formattedPhone !== phone"
        class="text-sm text-green-600"
      >
        Formatted: {{ formattedPhone }}
      </div>
    </div>

    <!-- Message -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700">
        Message (Optional)
      </label>

      <div class="relative">
        <textarea
          v-model="message"
          placeholder="Enter your message here..."
          rows="4"
          :maxlength="maxMessageLength"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors resize-none"
          :class="{
            'border-red-300 focus:ring-red-500 focus:border-red-500':
              messageValidation,
          }"
        />

        <!-- Character count -->
        <div class="absolute bottom-2 right-2 text-xs text-gray-500">
          {{ messageLength }}/{{ maxMessageLength }}
        </div>
      </div>

      <!-- Message validation -->
      <div
        v-if="messageValidation"
        class="flex items-center space-x-1 text-sm text-red-600"
      >
        <ExclamationCircleIcon class="w-4 h-4" />
        <span>{{ messageValidation }}</span>
      </div>
    </div>

    <!-- Message Templates -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700"
        >Message Templates</label
      >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          v-for="template in messageTemplates"
          :key="template"
          @click="useTemplate(template)"
          class="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors text-left"
          type="button"
        >
          {{ template }}
        </button>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex space-x-2">
      <button
        @click="clearForm"
        class="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
        type="button"
      >
        Clear Form
      </button>
    </div>

    <!-- Preview -->
    <div v-if="smsUrl" class="bg-green-50 p-3 rounded-lg">
      <h4 class="text-sm font-medium text-green-800 mb-1">SMS Preview:</h4>
      <p class="text-sm text-green-700 font-mono break-all">{{ smsUrl }}</p>
    </div>

    <!-- Tips -->
    <div class="bg-blue-50 p-3 rounded-lg">
      <h4 class="text-sm font-medium text-blue-800 mb-1">Tips:</h4>
      <ul class="text-sm text-blue-700 space-y-1">
        <li>• Include country code for international numbers</li>
        <li>• Keep messages under 160 characters for single SMS</li>
        <li>• Test the number before generating QR code</li>
      </ul>
    </div>
  </div>
</template>